import { useState } from "react";
import { Link } from "react-router-dom";

const faqs = [
  {
    q: "What is ClickSendAI and how does it work?",
    a: "ClickSendAI is an AI-powered marketing automation platform built specifically for Skool community owners. You connect your Skool group, and our AI analyzes your content to generate platform-optimized posts. These posts are then automatically published across 30+ social media, video, image, blog, and directory networks on a schedule you control — driving new members to your Skool group on autopilot.",
  },
  {
    q: "Which social networks does ClickSendAI support?",
    a: "ClickSendAI supports 30+ networks including Facebook, Instagram, Twitter/X, LinkedIn, YouTube, TikTok, Pinterest, Reddit, Medium, WordPress, Tumblr, Telegram, and many more social, video, image sharing, blog, and business directory sites. We're constantly adding new integrations. Check our Features page for the full up-to-date list.",
  },
  {
    q: "Do I need any marketing or technical experience?",
    a: "Absolutely not. ClickSendAI is designed for Skool community owners, not marketers. The AI handles content creation, scheduling, and publishing automatically. You just connect your Skool group, choose your networks, and the platform does the rest. Most users are fully set up and posting within 5 minutes.",
  },
  {
    q: "How does the AI generate content for my Skool group?",
    a: "Our AI (powered by GPT-4) analyzes your Skool group's topics, posts, and audience to understand your unique brand voice and niche. It then generates platform-specific content — tailored captions, hashtags, and post formats for each network — that sounds authentically like you and resonates with your target audience.",
  },
  {
    q: "Can I review and edit posts before they go live?",
    a: "Yes! You have full control. You can review every AI-generated post in your content library, edit them, approve them, or schedule them manually. You can also set up auto-approval for fully hands-off publishing, or require manual review for every post — it's completely up to you.",
  },
  {
    q: "Is there a free trial? Do I need a credit card?",
    a: "Yes, every plan comes with a 7-day free trial. You get full access to all features during your trial so you can see real results before committing to a paid plan.",
  },
  {
    q: "What happens to my data if I cancel?",
    a: "Your data is yours. If you cancel, you can export all your posts, analytics, and account data at any time. We retain your data for 30 days after cancellation in case you change your mind, then it's permanently deleted.",
  },
  {
    q: "Can I manage multiple Skool groups?",
    a: "Yes! Our Agency plan supports up to 25 Skool groups from a single dashboard. This is perfect for coaches, consultants, or agencies managing multiple communities. Each group gets its own connected networks, content library, and analytics.",
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section id="faq" className="py-24 bg-gradient-to-b from-white to-slate-50">
      <div className="max-w-3xl mx-auto px-5 md:px-8">
        <div className="text-center mb-14">
          <span className="inline-block px-4 py-1.5 rounded-full bg-violet-100 text-violet-700 text-xs font-semibold uppercase tracking-wider mb-4">
            FAQ
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-500">
            Everything you need to know about ClickSendAI. Can&apos;t find your answer?{" "}
            <Link to="/dashboard/support" className="text-violet-600 hover:underline cursor-pointer">Contact our support team.</Link>
          </p>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className={`rounded-xl border transition-all duration-200 overflow-hidden ${openIndex === i ? "border-violet-200 bg-violet-50/40" : "border-gray-100 bg-white"}`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
              >
                <span className="text-sm font-semibold text-gray-900 pr-4">{faq.q}</span>
                <div className="w-6 h-6 flex items-center justify-center shrink-0">
                  <i className={`${openIndex === i ? "ri-subtract-line text-violet-600" : "ri-add-line text-gray-400"} text-lg transition-all`}></i>
                </div>
              </button>
              {openIndex === i && (
                <div className="px-5 pb-5">
                  <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Schema.org FAQPage structured data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": faqs.map((faq) => ({
                "@type": "Question",
                "name": faq.q,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.a,
                },
              })),
            }),
          }}
        />
      </div>
    </section>
  );
}
